import AuturaDropdown from 'components/inputs/AuturaDropdown';
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Slide,
} from '@mui/material';
import Button from '@mui/material/Button';
import { Cancel, Send } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import { ChangeEvent, forwardRef, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cardHeaderSx, cardSx, mobileCardSx } from 'utils/styleProps';
import { Job } from 'types';
import {
  formatLicensePlateString,
  formatVehicleString,
} from 'utils/stringFormatters';
import { capitalize } from 'utils';
import { CancelReason } from 'api';
import { TransitionProps } from '@mui/material/transitions';
import { JobActionHandler } from './jobsAction';
import { useFetcher } from 'react-router';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { ROUTES, routeWithParams } from 'router/routes';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='left' ref={ref} {...props} />;
});

interface CancelJobDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  job: Job;
  cancelReasons: CancelReason[];
  fetcherKey: string;
}

const CancelJobDialog = ({
  isOpen,
  handleClose,
  job,
  cancelReasons,
  fetcherKey,
}: CancelJobDialogProps): ReactNode => {
  const { t } = useTranslation();
  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('lg'));
  const [reason, setReason] = useState<string>('');
  const fetcher = useFetcher({ key: fetcherKey });
  const handleSubmit = () => {
    const selected = cancelReasons.find(
      (r) => r.description.toLowerCase() === reason?.toLowerCase(),
    );
    if (!selected) return;

    fetcher.submit(
      {
        handler: JobActionHandler.CANCEL,
        jobID: job.id,
        tenantID: job.tenantID,
        reasonID: selected.key,
        reason: selected.description,
      },
      {
        method: 'PUT',
        action: routeWithParams(ROUTES.JOBS, [job.tenantID]),
        encType: 'application/json',
      },
    );
  };

  const handleReasonChange = (e: ChangeEvent<HTMLInputElement>) => {
    const reasonStr = e.target.value;
    const reason = cancelReasons.find((r) => r.description === reasonStr);
    if (reason) {
      setReason(reason.description);
    } else {
      setReason('');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-label={`${t('dialogs.cancel.header', {
        ns: 'jobs',
        type: capitalize(job.type),
      })} Dialog`}
      TransitionComponent={Transition}
      fullScreen={isMobile}
      sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
      maxWidth={'sm'}
    >
      <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
        <Card sx={isMobile ? mobileCardSx : cardSx}>
          <Box
            sx={
              isMobile
                ? {
                    display: 'flex',
                    p: { xs: 2, sm: 4 },
                    backgroundColor: palette.secondary.main,
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                  }
                : cardHeaderSx
            }
          >
            <Box sx={{ flex: 3, alignItems: 'center', display: 'flex' }}>
              <Typography
                variant={'h4'}
                sx={{
                  color: palette.secondary.contrastText,
                  textAlign: 'left',
                }}
              >
                {t('dialogs.cancel.header', {
                  ns: 'jobs',
                  type: capitalize(job.type),
                })}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              <Button
                variant={'text'}
                onClick={handleClose}
                sx={{
                  px: 1,
                  textTransform: 'uppercase',
                  color: palette.secondary.contrastText,
                }}
                endIcon={
                  <Cancel
                    htmlColor={palette.secondary.contrastText}
                    fontSize={'large'}
                  />
                }
              >
                {t('close')}
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              p: 2,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Grid container spacing={0}>
              <Grid size={12} sx={{ mb: 2 }}>
                {cancelReasons.length > 0 ? (
                <Typography variant={'body1'}>
                  {t('dialogs.cancel.body', { ns: 'jobs' })}
                </Typography>
                ) : (
                  <Typography variant={'body1'} color={'error'}>
                    {t('dialogs.cancel.reasons_error', { ns: 'jobs' })}
                  </Typography>
                )}
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  sm: 4,
                }}
              >
                <Typography variant={'h6'}>{`${t('job.property.label', {
                  ns: 'entities',
                })}:`}</Typography>
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  sm: 8,
                }}
              >
                <Typography variant={'body1'} fontWeight={'bold'}>
                  {job.propertyName}
                </Typography>
              </Grid>
              {formatVehicleString(job.vehicle) && (
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                }}
              >
                <Typography variant={'h6'}>{`${t('job.vehicle.label', {
                  ns: 'entities',
                })}:`}</Typography>
              </Grid>
              )}
              {formatVehicleString(job.vehicle) && (
                <Grid
                  sx={{ mb: { xs: 2, lg: 0 } }}
                  size={{
                    xs: 12,
                    sm: 8,
                  }}
                >
                  <Typography variant={'body1'} fontWeight={'bold'}>
                    {formatVehicleString(job.vehicle)}
                  </Typography>
                </Grid>
              )}
              
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                }}
              >
                <Typography variant={'h6'}>{`${t('job.vehicle.plate.label', {
                  ns: 'entities',
                })}:`}</Typography>
              </Grid>
              <Grid
                sx={{ mb: 4 }}
                size={{
                  xs: 12,
                  sm: 8,
                }}
              >
                <Typography variant={'body1'} fontWeight={'bold'}>
                  {formatLicensePlateString(job.plate) || '--'}
                </Typography>
              </Grid>
              <Grid size={12}>
                <AuturaDropdown
                  name='cancellationReason'
                  label={t('dialogs.cancel.reason', { ns: 'jobs' })}
                  value={reason}
                  disabled={fetcher.state !== 'idle' || cancelReasons.length === 0}
                  required
                  setValue={handleReasonChange}
                  options={cancelReasons.map((r) => r.description)}
                />
              </Grid>
              <Stack
                width='100%'
                direction='row'
                spacing={2}
                justifyContent={{ xs: 'space-between', lg: 'flex-end' }}
                mt={4}
                display='flex'
                alignItems='flex-end'
                alignContent='flex-end'
              >
                <Button
                  variant={'outlined'}
                  onClick={handleClose}
                  color={'secondary'}
                  endIcon={<Cancel />}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant={'contained'}
                  type='submit'
                  color={'secondary'}
                  endIcon={<Send />}
                  disabled={!reason || fetcher.state !== 'idle'}
                >
                  {t('submit')}
                </Button>
              </Stack>
            </Grid>
          </Box>
        </Card>
      </ValidatorForm>
    </Dialog>
  );
};
export default CancelJobDialog;
